import { useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { useSiteContent } from "../context/ContentContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronDown, MapPin, Mail, Phone, Clock, Check } from "lucide-react";
import api from "../lib/api";
import ProtectedEmailLink from "../components/ProtectedEmailLink";

const intents = ["Buy", "Sell", "Both", "General enquiry"];
const metals = ["Gold", "Silver", "Both"];

export default function Contact() {
  const { content } = useSiteContent();
  const { company, faqs, contact_page } = content;
  const pageCopy = contact_page || {};
  const [form, setForm] = useState({
    full_name: "", company: "", country: "", email: "", phone: "",
    intent: "Buy", metal: "Gold", product_quantity: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please fill in name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Enquiry sent — our team will respond shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not send your enquiry. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={pageCopy.header_eyebrow}
        title={
          <>
            {pageCopy.header_title_part1}{" "}
            <span className="theme-text-accent">{pageCopy.header_title_highlight}</span>
            {pageCopy.header_title_part2}
          </>
        }
        intro={pageCopy.header_intro}
      />

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="lg:col-span-3">
            <motion.form initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              onSubmit={submit} className="theme-surface rounded-3xl p-5 sm:p-8 md:p-10 shadow-sm" data-testid="quote-form">
              {done ? (
                <div className="text-center py-10" data-testid="quote-form-success">
                  <div className="w-16 h-16 rounded-full theme-icon-chip mx-auto flex items-center justify-center">
                    <Check className="w-7 h-7" />
                  </div>
                  <h3 className="font-display text-2xl font-bold theme-text-primary mt-5">{pageCopy.success_title}</h3>
                  <p className="theme-text-secondary mt-3 max-w-md mx-auto">
                    {pageCopy.success_body}
                  </p>
                  <button className="btn-secondary mt-6" onClick={() => { setDone(false); setForm({ full_name: "", company: "", country: "", email: "", phone: "", intent: "Buy", metal: "Gold", product_quantity: "", message: "" }); }}>
                    {pageCopy.success_button}
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold theme-text-primary mb-2">{pageCopy.form_title}</h2>
                  <p className="theme-text-muted text-sm mb-8">{pageCopy.form_required_note}</p>

                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Full name *">
                      <input className="input-x" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required data-testid="quote-input-name" />
                    </Field>
                    <Field label="Company">
                      <input className="input-x" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="quote-input-company" />
                    </Field>
                    <Field label="Country">
                      <input className="input-x" value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="quote-input-country" />
                    </Field>
                    <Field label="Email *">
                      <input type="email" className="input-x" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="quote-input-email" />
                    </Field>
                    <Field label="Phone">
                      <input className="input-x" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="quote-input-phone" />
                    </Field>
                    <Field label="I want to">
                      <SelectPills options={intents} value={form.intent} onChange={(v) => set("intent", v)} testid="quote-intent" />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Metal">
                      <SelectPills options={metals} value={form.metal} onChange={(v) => set("metal", v)} testid="quote-metal" />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Product & quantity">
                      <input className="input-x" placeholder="e.g. 5kg gold cast bars, 99.9%" value={form.product_quantity} onChange={(e) => set("product_quantity", e.target.value)} data-testid="quote-input-product" />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Message">
                      <textarea rows={5} className="input-x" placeholder="Destination, target delivery, additional context…"
                        value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="quote-input-message" />
                    </Field>
                  </div>

                  <p className="text-xs theme-text-muted mt-6">
                    {pageCopy.privacy_note}
                  </p>
                  <button type="submit" className="btn-primary mt-6 w-full justify-center" disabled={submitting} data-testid="quote-submit-button">
                    {submitting ? pageCopy.submitting_label : pageCopy.submit_label}
                  </button>
                </>
              )}
            </motion.form>
          </div>

          <div className="lg:col-span-2 space-y-5">
            <div className="rounded-3xl theme-gradient-dark p-6 sm:p-8">
              <div className="eyebrow mb-4" style={{ color: "var(--gold-hover)" }}>{pageCopy.contact_card_eyebrow}</div>
              <h3 className="font-display text-2xl font-bold mb-6">{pageCopy.contact_card_title}</h3>
              <ul className="space-y-5 text-sm theme-panel-dark-muted">
                <li className="flex items-start gap-3"><MapPin className="w-4 h-4 theme-text-accent mt-1" /> {company.address}</li>
                <li className="flex items-start gap-3"><Phone className="w-4 h-4 theme-text-accent mt-1" /> {company.phone}</li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 theme-text-accent mt-1" />
                  <ProtectedEmailLink className="hover:text-[var(--gold-hover)] transition-colors" testid="contact-email" />
                </li>
                <li className="flex items-start gap-3"><Clock className="w-4 h-4 theme-text-accent mt-1" /> {company.hours}</li>
              </ul>
            </div>
            <div className="rounded-3xl theme-surface p-6 sm:p-8">
              <div className="eyebrow mb-3">{pageCopy.response_eyebrow}</div>
              <p className="theme-text-secondary text-sm leading-relaxed">
                {pageCopy.response_body}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="theme-bg-secondary section-pad">
        <div className="container-x">
          <div className="eyebrow mb-4">{pageCopy.faqs_eyebrow}</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold theme-text-primary tracking-tight mb-10 sm:mb-12 max-w-3xl">
            {pageCopy.faqs_title_part1}{" "}
            <span className="theme-text-accent">{pageCopy.faqs_title_highlight}</span>
            {pageCopy.faqs_title_part2 ? ` ${pageCopy.faqs_title_part2}` : ""}
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((f, i) => <FaqItem key={i} faq={f} index={i} />)}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-x">{label}</label>
      {children}
    </div>
  );
}

function SelectPills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button type="button" key={o} onClick={() => onChange(o)} data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
            value === o ? "bg-[var(--interactive-bg)] text-[var(--interactive-text)] border-[var(--interactive-bg)]" : "theme-bg-primary theme-text-primary border-[var(--border-light)] hover:border-[var(--gold)] hover:text-[var(--gold)]"
          }`}>
          {o}
        </button>
      ))}
    </div>
  );
}

function FaqItem({ faq, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="theme-surface rounded-2xl overflow-hidden" data-testid={`faq-item-${index}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-start justify-between gap-4 px-5 sm:px-6 py-5 text-left">
        <span className="font-display text-base sm:text-lg theme-text-primary">{faq.q}</span>
        <ChevronDown className={`w-5 h-5 theme-text-accent transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-[max-height] duration-300 ${open ? "max-h-96" : "max-h-0"}`}>
        <div className="px-6 pb-6 theme-text-secondary leading-relaxed">{faq.a}</div>
      </div>
    </div>
  );
}
